/**
 * Privacidade & Dados (LGPD).
 *
 * A partir da 0.1.22 a senha é ÚNICA (a do login). Não há mais uma "senha de
 * acesso" separada aqui — o acesso ao app é pela conta do usuário, e as ações
 * sensíveis (backup, apagar dados) confirmam com a MESMA senha de login.
 * Esta tela mantém: apagar dados de um condomínio e aviso de privacidade.
 * (A "migração para outro desktop" saiu na 0.1.85 com a versão desktop.)
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, FileText, Lock, ShieldCheck, Trash2 } from "lucide-react";
import { useState } from "react";
import { api, type CondominioListItem } from "../lib/api";
import { EliminarDadosModal } from "./EliminarDadosModal";
import { PrivacidadeCard } from "./PrivacidadeCard";

export function LgpdPage({ onVoltar }: { onVoltar: () => void }) {
  const qc = useQueryClient();

  // Apagar dados de um condomínio
  const { data: conds } = useQuery<CondominioListItem[]>({
    queryKey: ["condominios"],
    queryFn: () => api.get<CondominioListItem[]>("/condominios"),
  });
  const [condSel, setCondSel] = useState("");
  const [modalEliminar, setModalEliminar] = useState(false);

  return (
    <div className="max-w-3xl mx-auto">
      <button
        type="button"
        onClick={onVoltar}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft size={16} />
        Voltar
      </button>

      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={22} className="text-brand-700" />
        <h2 className="text-2xl font-semibold text-slate-900">Privacidade &amp; Dados</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Medidas de proteção dos dados (LGPD). Os dados ficam neste computador (ou no seu
        servidor), sob responsabilidade da administradora.
      </p>

      {/* Nota: senha única (login) */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <div className="flex items-center gap-2 mb-1">
          <Lock size={17} className="text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-900">Senha única (a do login)</h3>
        </div>
        <p className="text-[13px] text-slate-600">
          O acesso ao app é pela sua <b>conta (login)</b> — uma senha só. As ações
          sensíveis (fazer <b>backup</b>, <b>apagar dados</b> e <b>migrar</b>) pedem a
          <b> mesma senha de login</b> para confirmar. Para <b>trocar sua senha</b> (ou
          se esqueceu), peça ao <b>administrador</b> — ele redefine na tela
          <b> Usuários</b>. Se <b>você é o administrador (Dono)</b>, use a tela
          <b> Usuários</b> (no cabeçalho) para trocar a sua senha e a dos demais.
        </p>
      </div>

      {/* Apagar dados (funcional) */}
      <h3 className="text-sm font-semibold text-slate-900 mt-8 mb-2 flex items-center gap-2">
        <Trash2 size={16} className="text-rose-500" /> Apagar dados de um condomínio
      </h3>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5">
        <p className="text-[13px] text-slate-600 mb-3">
          Remove <b>permanentemente</b> todo o registro de um condomínio (meses,
          notas, cotas, PDFs e leituras de OCR) e gera um <b>relatório de exclusão
          (LGPD)</b>. Só o <b>síndico</b> deste condomínio ou o <b>Dono</b> podem iniciar,
          com a sua senha, e a exclusão exige a <b>aprovação por um código enviado por
          e-mail</b> ao Dono e ao conselho. Ação irreversível.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={condSel}
            onChange={(e) => setCondSel(e.target.value)}
            className="px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
          >
            <option value="">Escolha o condomínio…</option>
            {conds?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!condSel}
            onClick={() => setModalEliminar(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-40 text-sm font-medium rounded-md"
          >
            <Trash2 size={14} /> Apagar dados deste condomínio
          </button>
        </div>
      </div>

      {/* Aviso de privacidade */}
      <h3 className="text-sm font-semibold text-slate-900 mt-8 mb-2 flex items-center gap-2">
        <FileText size={16} className="text-violet-600" /> Aviso de privacidade
      </h3>
      <PrivacidadeCard />

      {modalEliminar && condSel && (
        <EliminarDadosModal
          condominioId={Number(condSel)}
          onFechar={() => setModalEliminar(false)}
          onConcluido={() => {
            setModalEliminar(false);
            setCondSel("");
            qc.invalidateQueries({ queryKey: ["condominios"] });
          }}
        />
      )}
    </div>
  );
}
